import { useEffect } from "react";
import { cn } from "../../lib/utils.ts";
import { Button } from "./button.tsx";

/**
 * The bottom sheet from the design: a scrim that closes on tap, and a paper
 * panel with a 3px ink rule on top. Padded past the home indicator so the
 * primary button is never under the iOS gesture bar.
 */
export function Sheet({
  open,
  title,
  emoji,
  onClose,
  children,
  className,
}: {
  open: boolean;
  title: string;
  /** Shown before the title, matching the button that opened the sheet. */
  emoji?: string;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 z-20 bg-scrim" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn(
          "fixed inset-x-0 bottom-0 z-30 mx-auto w-full max-w-[480px] border-t-[3px] border-ink bg-paper px-5 pt-5 shadow-sheet md:max-w-[560px]",
          className,
        )}
        style={{ paddingBottom: "calc(34px + env(safe-area-inset-bottom))" }}
      >
        <div className="flex items-baseline">
          <h2 className="heading text-[22px]">
            {emoji ? (
              <span aria-hidden="true" className="mr-2">
                {emoji}
              </span>
            ) : null}
            {title}
          </h2>
          <Button variant="ghost" className="ml-auto -mr-2 text-[13px]" onClick={onClose}>
            Cancel
          </Button>
        </div>
        {children}
      </div>
    </>
  );
}
